import styled from '@emotion/styled'
import React, { useState } from 'react'

import { CardContent, Card as MuiCard, Typography } from '@mui/material'
import { Box, spacing } from '@mui/system'
import { useTheme } from '@mui/material/styles'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { enUS, svSE } from '@mui/x-data-grid-pro/locales'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { formatBigMoney, formatDateYYYYMMDD, formatDateYYYYMMDDhhmmss } from '../../packages/utils/numberFormatters'
import { CustomDataGridToolbar } from '../custom-grid-components/CustomDataGridToolbar'
import { convertibleGridCurrentPageAtom, convertibleSearchTextfieldAtom } from '../../atoms/convertibleAtom'
import { ConvertibleEntity } from 'types/ConvertibleEntity'

const Card = styled(MuiCard)(spacing)

function NoConvertibles() {
    return (
        <Card mb={6}>
            <CardContent>
                <Typography variant='h6' gutterBottom>
                    Inga konvertibler hittades
                </Typography>
            </CardContent>
        </Card>
    )
}

const gridTextI18n = (locale: string) => {
    if (locale?.startsWith('sv')) {
        return svSE.components.MuiDataGrid.defaultProps.localeText
    } else {
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga konvertibler hittades</Typography>
        </Box>
    )
}

type ConvertiblesTabsProps = {
    convertibles: ConvertibleEntity[]
    fields: string[]
    detailsPath?: ((convertible: ConvertibleEntity) => string) | null
    sortModel?:
        | {
              field: string
              sort: string
          }[]
        | undefined
    pagination?: boolean
    pageSizeInitialState?: number
    pageSizeOptions?: number[]
}

function ConvertiblesGrid({
    convertibles,
    fields,
    detailsPath = null,
    sortModel = undefined,
    pagination = true,
    pageSizeInitialState = 10,
    pageSizeOptions = [10, 25, 50, 100],
}: ConvertiblesTabsProps) {
    const [convertibleGridCurrentPage, setConvertibleGridCurrentPage] = useAtom(convertibleGridCurrentPageAtom)
    const [searchTextfield, setSearchTextfield] = useAtom(convertibleSearchTextfieldAtom)

    const [pageSize, setPageSize] = useState(pageSizeInitialState)

    // Initialize filterModel with quickFilterValues and an empty items array
    const [filterModel, setFilterModel] = useState({
        items: [], // Ensure items is an empty array initially
        quickFilterValues: [searchTextfield], // Default quick filter value
    })

    const navigate = useNavigate()
    const theme = useTheme()
    const { i18n } = useTranslation()
    const locale = i18n.language

    detailsPath = detailsPath || ((convertible: ConvertibleEntity): string => `/convertibles/${convertible.id}`)

    const handleFilterModelChange = (newFilterModel) => {
        setFilterModel({
            ...newFilterModel,
            items: newFilterModel.items || [], // Ensure items is always an array
        })
        setConvertibleGridCurrentPage(0)
        setSearchTextfield(newFilterModel.quickFilterValues[0])
    }

    const cols = [
        {
            field: 'convertibleName',
            headerName: 'Namn',
            flex: 1,
            renderCell: (params) => <Link>{params.row.convertibleName}</Link>,
        },
        {
            field: 'loanAmount',
            headerName: 'Belopp',
            flex: 1,
            sortable: false,
            renderCell: (params) =>
                formatBigMoney(params.row.loanAmount, { locale, currencySymbol: params.row.currency }) || '-',
        },
        {
            field: 'maturityDate',
            headerName: 'Förfallodatum',
            flex: 1,
            valueFormatter: (value) => formatDateYYYYMMDD(value),
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1)
                const date2 = new Date(v2)
                return date1 - date2
            },
        },
        {
            field: 'publicationDate',
            headerName: 'Publiceringsdatum',
            flex: 1,
            valueFormatter: (value) => formatDateYYYYMMDD(value),
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1)
                const date2 = new Date(v2)
                return date1 - date2
            },
        },
        {
            field: 'updatedAt',
            headerName: 'Uppdaterad',
            flex: 1,
            valueFormatter: (value) => formatDateYYYYMMDDhhmmss(value),
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1)
                const date2 = new Date(v2)
                return date1.getTime() - date2.getTime()
            },
        },
    ]

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    if (!convertibles || convertibles.length === 0) {
        return <NoConvertibles />
    }

    return (
        // adapt filter: https://mui.com/x/react-data-grid/filtering/customization/#create-a-custom-operator
        <DataGridPro
            sx={{
                ...disableSelectedSellOutlineStyle,
                width: '100%',
                '& .even': {
                    backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                },
                '& .odd': {
                    // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                },
                '& .MuiDataGrid-cell': {
                    paddingY: 4,
                },
            }}
            localeText={gridTextI18n(locale)}
            pagination={pagination}
            initialState={{
                sorting: { sortModel },
            }}
            pageSizeOptions={pageSizeOptions}
            disableColumnMenu
            disableRowSelectionOnClick
            rows={convertibles}
            autoHeight
            getRowHeight={() => 'auto'}
            columns={fields ? cols.filter((c) => fields.includes(c.field)) : cols}
            density='standard'
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
            slots={{ noResultsOverlay: CustomNoResultsOverlay, toolbar: CustomDataGridToolbar }}
            paginationModel={{ pageSize, page: convertibleGridCurrentPage }}
            onPaginationModelChange={({ page, pageSize }) => {
                setConvertibleGridCurrentPage(page)
                setPageSize(pageSize)
            }}
            filterModel={filterModel}
            // Capture changes to the filter model
            onFilterModelChange={handleFilterModelChange}
            showToolbar
            onCellClick={(params, event) => {
                const path =
                    params.field === 'shareName' ? `/companies/${params.row.companyId}` : detailsPath(params.row)
                if (params.row.id) {
                    if (event.ctrlKey || event.metaKey) {
                        window.open(path, '_blank')
                    } else {
                        navigate(path)
                    }
                }
            }}
        />
    )
}

export default ConvertiblesGrid
